import React, { useState, useEffect, useCallback } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import MediaUploadInput from "@/components/MediaUploadInput";
import GroupsMultiSelect from "@/components/GroupsMultiSelect";
import { Shield, Save, Bell, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";

const DEFAULTS = {
  anti_link: false,
  anti_link_admin_exempt: true,
  welcome_enabled: false,
  welcome_message: "👋 ¡Bienvenido/a {user} a {group}! Escribe .menu para ver las opciones disponibles.",
  welcome_media_type: "text",
  welcome_media_url: "",
  welcome_show_profile_pic: true,
  rules_message: "",
  only_admin_sales: false,
  admin_notify_phone: "",
  admin_notify_on_humano: false,
  admin_notify_on_order: false,
  pedido_enabled: true,
  pedido_disabled_message: "🛍️ Para hacer un pedido, por favor contáctame directamente en privado. Te atenderé personalmente.",
  builtin_sales_groups: [],
  allowed_groups: [],
};

export default function GroupControlsPage() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [phoneCheck, setPhoneCheck] = useState({ status: "idle", exists: false });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`${API}/group-controls`);
      setForm({ ...DEFAULTS, ...data });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  // Live WhatsApp number validation (debounced)
  useEffect(() => {
    const phone = (form.admin_notify_phone || "").replace(/\D/g, "");
    if (!phone || phone.length < 7) {
      setPhoneCheck({ status: "idle", exists: false });
      return;
    }
    setPhoneCheck({ status: "checking", exists: false });
    const t = setTimeout(async () => {
      try {
        const { data } = await authAxios.get(`${API}/whatsapp/check-number`, { params: { phone } });
        setPhoneCheck({ status: data.exists ? "valid" : "invalid", exists: !!data.exists });
      } catch {
        setPhoneCheck({ status: "error", exists: false });
      }
    }, 700);
    return () => clearTimeout(t);
  }, [form.admin_notify_phone]);

  const save = async () => {
    setSaving(true);
    try {
      await authAxios.put(`${API}/group-controls`, form);
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } finally { setSaving(false); }
  };

  const resetAllTakeovers = async () => {
    if (!window.confirm("¿Reanudar el bot en TODOS los chats que estén silenciados? Esto limpia cualquier 'Pausar bot' activo (manual o de versiones antiguas).")) return;
    try {
      const { data } = await authAxios.delete(`${API}/takeover`);
      alert(`✅ Bot reanudado en ${data.cleared} chat(s). Todas las conversaciones ahora reciben respuestas automáticas de nuevo.`);
    } catch (e) {
      alert(e.response?.data?.detail || "No se pudo reanudar");
    }
  };

  const testNotification = async () => {
    if (!form.admin_notify_phone) {
      alert("Primero configura tu número y guarda los cambios.");
      return;
    }
    try {
      const { data } = await authAxios.post(`${API}/notifications/test`);
      alert(`✅ ${data.message || "Notificación enviada. Revisa tu WhatsApp."}`);
    } catch (e) {
      alert(`❌ ${e.response?.data?.detail || "No se pudo enviar la prueba"}\n\nPosibles causas:\n• El bot no está conectado a WhatsApp\n• El número tiene formato incorrecto\n• El número no tiene WhatsApp activo\n• No has guardado los cambios`);
    }
  };

  if (loading) return <div className="p-8"><div className="animate-pulse h-64 bg-slate-200 rounded-xl" /></div>;

  return (
    <div className="p-8 max-w-3xl mx-auto" data-testid="group-controls-page">
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Shield className="w-7 h-7" /> Controles de Grupo
          </h1>
          <p className="text-slate-500 mt-1">Moderación automática, bienvenida y modo solo-admin</p>
        </div>
        <Button
          variant="outline"
          onClick={resetAllTakeovers}
          className="text-amber-700 border-amber-300 hover:bg-amber-50"
          data-testid="reset-takeovers-btn"
        >
          <RotateCcw className="w-4 h-4 mr-2" /> Reanudar bot en todos los chats
        </Button>
      </div>

      <Card className="mb-4 border-red-300 bg-red-50/50">
        <CardContent className="p-6">
          <div className="flex items-start gap-3 mb-3">
            <div className="text-2xl">🔒</div>
            <div className="flex-1">
              <Label className="text-base font-semibold text-red-900">Grupos donde el bot tiene permitido publicar</Label>
              <p className="text-sm text-red-800/80 mt-1">
                <strong>Whitelist global y estricta.</strong> El bot <strong>SOLO</strong> publicará en los grupos que elijas aquí. Si la lista está <strong>vacía</strong>, el bot quedará completamente en silencio en TODOS los grupos (no responderá a comandos, palabras clave, .pedido, anti-link, ni bienvenida en ningún grupo).
              </p>
            </div>
          </div>
          <GroupsMultiSelect
            value={form.allowed_groups || []}
            onChange={(ids) => setForm({ ...form, allowed_groups: ids })}
            label="Grupos permitidos (lista blanca)"
            helpText={(form.allowed_groups || []).length === 0
              ? "⚠️ Vacío = el bot NO responde en ningún grupo. En chats privados sigue funcionando normal."
              : `✅ El bot sólo actuará en estos ${form.allowed_groups.length} grupo(s). En el resto quedará en silencio.`
            }
            testIdPrefix="gc-allowed-groups"
          />
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardContent className="p-6 space-y-6">
          {/* Anti-link */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <Label className="text-base font-medium">🔗 Anti-enlaces</Label>
                <p className="text-sm text-slate-500">Borra mensajes con enlaces (el bot debe ser admin)</p>
              </div>
              <Switch checked={!!form.anti_link} onCheckedChange={v => setForm({ ...form, anti_link: v })} data-testid="gc-anti-link" />
            </div>
            {form.anti_link && (
              <div className="flex items-center justify-between pl-4 pt-2 border-l-2 border-slate-200">
                <Label>Excluir admins</Label>
                <Switch checked={!!form.anti_link_admin_exempt} onCheckedChange={v => setForm({ ...form, anti_link_admin_exempt: v })} />
              </div>
            )}
          </div>

          <hr />

          {/* Welcome */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <Label className="text-base font-medium">👋 Mensaje de bienvenida</Label>
                <p className="text-sm text-slate-500">Saluda a nuevos miembros del grupo automáticamente</p>
              </div>
              <Switch checked={!!form.welcome_enabled} onCheckedChange={v => setForm({ ...form, welcome_enabled: v })} data-testid="gc-welcome" />
            </div>
            {form.welcome_enabled && (
              <div className="pl-4 pt-2 border-l-2 border-slate-200 space-y-4">
                <div>
                  <Label className="text-xs text-slate-600">Texto del mensaje</Label>
                  <Textarea rows={3} value={form.welcome_message}
                    onChange={e => setForm({ ...form, welcome_message: e.target.value })}
                    placeholder="Usa {user} para mencionar y {group} para el nombre del grupo"
                    data-testid="gc-welcome-message" />
                  <p className="text-xs text-slate-500 mt-1">Variables: {"{user}"}, {"{group}"}</p>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">📸 Mostrar foto de perfil del nuevo miembro</Label>
                    <p className="text-xs text-slate-500">Se envía la foto del usuario con el texto como pie de foto</p>
                  </div>
                  <Switch
                    checked={!!form.welcome_show_profile_pic}
                    onCheckedChange={v => setForm({ ...form, welcome_show_profile_pic: v })}
                    data-testid="gc-welcome-profile-pic"
                  />
                </div>

                <div>
                  <Label className="text-xs text-slate-600">Adjuntar un medio extra (imagen, video, audio o documento)</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    <Select value={form.welcome_media_type} onValueChange={v => setForm({ ...form, welcome_media_type: v })}>
                      <SelectTrigger data-testid="gc-welcome-media-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Sin medio (solo texto)</SelectItem>
                        <SelectItem value="image">Imagen</SelectItem>
                        <SelectItem value="video">Video</SelectItem>
                        <SelectItem value="audio">Audio</SelectItem>
                        <SelectItem value="document">Documento (PDF)</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="col-span-2">
                      {form.welcome_media_type !== "text" && (
                        <MediaUploadInput
                          value={form.welcome_media_url}
                          onChange={(v) => setForm({ ...form, welcome_media_url: v })}
                          accept={form.welcome_media_type === "image" ? "image/*" :
                                  form.welcome_media_type === "video" ? "video/*" :
                                  form.welcome_media_type === "audio" ? "audio/*" :
                                  "application/pdf"}
                          testIdPrefix="gc-welcome-media"
                        />
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Si tenés activada la foto de perfil <strong>y</strong> elegís un medio extra, se envían los <strong>dos</strong>:
                    primero la foto del nuevo miembro con el texto, y después el medio extra.
                  </p>
                </div>
              </div>
            )}
          </div>

          <hr />

          {/* Rules */}
          <div>
            <Label className="text-base font-medium">📜 Reglas del grupo</Label>
            <p className="text-sm text-slate-500 mb-2">Texto que se enviará con <code>.reglas</code></p>
            <Textarea rows={4} value={form.rules_message}
              onChange={e => setForm({ ...form, rules_message: e.target.value })}
              placeholder="1. Respeta a todos..." />
          </div>

          <hr />

          {/* Only admin sales */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">🔒 Solo admins pueden vender</Label>
              <p className="text-sm text-slate-500">No-admins solo pueden usar comandos de consulta</p>
            </div>
            <Switch checked={!!form.only_admin_sales} onCheckedChange={v => setForm({ ...form, only_admin_sales: v })} data-testid="gc-only-admin-sales" />
          </div>

          <hr />

          {/* Pedido enabled */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <Label className="text-base font-medium">🛒 Comando <code>.pedido</code> automático</Label>
                <p className="text-sm text-slate-500">Si lo apagas, el bot pedirá al cliente que te contacte en privado en lugar de iniciar el flujo automático</p>
              </div>
              <Switch
                checked={!!form.pedido_enabled}
                onCheckedChange={v => setForm({ ...form, pedido_enabled: v })}
                data-testid="gc-pedido-enabled"
              />
            </div>
            {!form.pedido_enabled && (
              <div className="pl-4 pt-2 border-l-2 border-amber-200">
                <Label className="text-xs text-slate-600">Mensaje cuando alguien escriba .pedido (modo manual)</Label>
                <Textarea
                  rows={2}
                  value={form.pedido_disabled_message}
                  onChange={e => setForm({ ...form, pedido_disabled_message: e.target.value })}
                  data-testid="gc-pedido-disabled-msg"
                />
              </div>
            )}
          </div>

          <hr />

          {/* Built-in sales commands per-group */}
          <div>
            <Label className="text-base font-medium">🛍️ Comandos integrados (<code>.catalogo</code>, <code>.stock</code>, <code>.pago</code>, <code>.pedido</code>)</Label>
            <p className="text-sm text-slate-500 mb-3">Elige en qué grupos los clientes pueden usar estos comandos automáticos. En chats privados siempre funcionan.</p>
            <GroupsMultiSelect
              value={form.builtin_sales_groups || []}
              onChange={(ids) => setForm({ ...form, builtin_sales_groups: ids })}
              label="Grupos donde se activan los comandos integrados"
              helpText="Vacío = se activan en TODOS los grupos donde el bot esté encendido."
              testIdPrefix="gc-builtin-groups"
            />
          </div>

          <hr />

          {/* Admin notifications */}
          <div>
            <Label className="text-base font-medium flex items-center gap-2">
              <Bell className="w-4 h-4" /> Notificaciones al admin
            </Label>
            <p className="text-sm text-slate-500 mb-3">Recibe un WhatsApp privado en tu número cuando ocurran eventos importantes.</p>

            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-600">Tu número de WhatsApp (formato internacional, sin + ni espacios)</Label>
                <div className="relative">
                  <Input
                    value={form.admin_notify_phone}
                    onChange={e => setForm({ ...form, admin_notify_phone: e.target.value.replace(/\D/g, "") })}
                    placeholder="521XXXXXXXXXX"
                    inputMode="numeric"
                    className="pr-32"
                    data-testid="gc-notify-phone"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs">
                    {phoneCheck.status === "checking" && <span className="text-slate-500" data-testid="phone-check-loading">⏳ Verificando...</span>}
                    {phoneCheck.status === "valid" && <span className="text-emerald-600 font-medium" data-testid="phone-check-valid">✅ Válido</span>}
                    {phoneCheck.status === "invalid" && <span className="text-red-600 font-medium" data-testid="phone-check-invalid">❌ Sin WhatsApp</span>}
                    {phoneCheck.status === "error" && <span className="text-amber-600" data-testid="phone-check-error">⚠️ Bot desconectado</span>}
                  </div>
                </div>
                <div className="text-xs text-slate-600 mt-2 bg-slate-50 p-2 rounded space-y-1">
                  <div>📋 <strong>Cómo formatear tu número:</strong></div>
                  <div>🇲🇽 <strong>México</strong>: <code>52</code> + número de 10 dígitos. Ejemplo: <code>521555123456</code> (el <code>1</code> después del 52 es obligatorio para móviles MX).</div>
                  <div>🇦🇷 <strong>Argentina</strong>: <code>549</code> + número. Ejemplo: <code>5491122334455</code></div>
                  <div>🇪🇸 <strong>España</strong>: <code>34</code> + número. Ejemplo: <code>34612345678</code></div>
                  <div>🇨🇴 <strong>Colombia</strong>: <code>57</code> + número. Ejemplo: <code>573001234567</code></div>
                  <div>⚠️ <strong>NO incluyas</strong>: signos +, espacios, guiones, paréntesis. <strong>SOLO dígitos.</strong></div>
                  <div>✅ El número debe tener WhatsApp activo (debe poder recibir mensajes desde el número del bot).</div>
                </div>
              </div>

              <div className="flex items-center justify-between pl-2">
                <Label className="text-sm">📨 Avisarme cuando alguien escriba <code>.humano</code>, <code>.agente</code> o <code>.contacto</code></Label>
                <Switch
                  checked={!!form.admin_notify_on_humano}
                  onCheckedChange={v => setForm({ ...form, admin_notify_on_humano: v })}
                  data-testid="gc-notify-humano"
                />
              </div>

              <div className="flex items-center justify-between pl-2">
                <Label className="text-sm">🛒 Avisarme cuando se complete un nuevo pedido</Label>
                <Switch
                  checked={!!form.admin_notify_on_order}
                  onCheckedChange={v => setForm({ ...form, admin_notify_on_order: v })}
                  data-testid="gc-notify-order"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving} data-testid="gc-save">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Guardando..." : "Guardar cambios"}
        </Button>
        {saved && <span className="text-emerald-600 text-sm">✓ Guardado</span>}
      </div>
    </div>
  );
}
